import * as Sentry from "@sentry/nextjs"
import { TextInput } from "flowbite-react"
import { Form, Formik, FormikProps } from "formik"
import _ from "lodash"
import moment, { Moment } from "moment"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Event, View } from "react-big-calendar"
import useSWR, { useSWRConfig } from "swr"
import * as Yup from "yup"

import MonoInlineDatePicker from "@components/elements/DatePickerInline/MonoInlineDatePicker"
import FormGroup from "@components/elements/FormGroup"
import MonoCalendar from "@components/elements/MonoCalendar/MonoCalendar"
import MonoSelect from "@components/elements/Select"
import MonoSelectWithCheckBox from "@components/elements/Select/MonoSelectWithCheckBox"
import { useDebounce } from "@hooks/useDebounce"
import useToast from "@hooks/useToast"
import SearchIcon from "@icons/Search"
import CheckoutModal from "@modules/widgets/components/CheckoutModal"
import { getSplittedDateRanges } from "@mono/essentials/entities/calendar/helpers"
import { usePublicGetAllEvents } from "@mono/essentials/hooks/WidgetHooks"
import * as CalendarServices from "@mono/essentials/services/CalendarServices"
import * as OrderServices from "@mono/essentials/services/OrderServices"
import * as ProductServices from "@mono/essentials/services/ProductServices"
import { EventAvailablilityADayType, SlotStatusEnum } from "@mono/essentials/types/CalendarTypes"
import { SelectOptionType } from "@mono/essentials/types/GlobalConfigTypes"
import { GetOrderDetailsResponseType } from "@mono/essentials/types/OrderTypes"
import { IDomain } from "@mono/essentials/types/UserTypes"
import { getDateInDomainTimezone, toLocalDate } from "@mono/essentials/utils/Common"

import OrderConfirmModal from "../OrderConfirmModal"
import ProductBuyModal from "../ProductBuyModal"
import ProductRentalBuyModal from "../ProductRentalBuyModal"
import SelectProductModal from "../SelectproductModal"

interface IClickedOption {
  action: string
  option: SelectOptionType
}

interface ICalendarPreviewData {
  calendar_for: string
  product: string
  show_product_in_calendar: string
  show_product_detail_page: string
  theme_brand_color: string
  selectedDate?: string
}

interface ICalendarType {
  showBigCalendar?: boolean
  selectedProduct?: string
  productView?: string
  isIntegratedPage?: boolean | undefined
  domainDetails?: IDomain
  nextStep?: string
  data: ICalendarPreviewData
  hideCustomerDetails?: boolean
}

interface ICalendarPageFilters {
  search: string
  categories: SelectOptionType[]
  status: string
  products: SelectOptionType[]
}
const NoSlotsAvailable = () => {
  return (
    <div className={"text14Semibold text-neutral-500 text-center mb-6"}>No slots available</div>
  )
}
const CalendarPreview = ({
  data,
  showBigCalendar,
  selectedProduct,
  productView,
  isIntegratedPage = false,
  domainDetails,
  nextStep,
  hideCustomerDetails = false,
}: ICalendarType) => {
  const { showToast } = useToast()
  const { cache } = useSWRConfig()
  const [show, setShow] = useState(false)
  const [selectedProductId, setShowProductSelectedId] = useState<string>("")
  const [existingCustomerSearchTerm, setExistingCustomerSearchTerm] = useState<string>("")
  const [showCheckoutModal, setShowCheckOutModal] = useState<boolean>(false)
  const debouncedSearchTerm = useDebounce(existingCustomerSearchTerm, 500)
  const [cartId, setcartId] = useState<string>("")
  const [orderData, setOrdertData] = useState<GetOrderDetailsResponseType | undefined>(undefined)
  const [orderConfirmModal, setOrderConfirmModal] = useState(false)
  const [showSelectModal, setShowSelectModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState<string>("")

  const [productFilterOptions, setProductFilterOptions] = useState<SelectOptionType[]>([
    {
      label: "All Products",
      value: "all",
    },
  ])
  const [categoryFilterOptions, setCategoryFilterOptions] = useState<SelectOptionType[]>([
    {
      label: "All Categories",
      value: "all",
    },
  ])
  const [shouldUpdateFilterOptions, setShouldUpdateFilterOptions] = useState(true)

  const [selectedDateRange, setSelectedDateRange] = useState<{
    from_date: string
    to_date: string
  }>({
    from_date: (getDateInDomainTimezone() as Moment).startOf("month").format("YYYY-MM-DD"),
    to_date: (getDateInDomainTimezone() as Moment).endOf("month").format("YYYY-MM-DD"),
  })
  const [selectedSlotStatus, setSelectedSlotStatus] = useState<SlotStatusEnum | "all">("all")
  const [availabilityMonth, setAvailabilityMonth] = useState<string | null>(null)
  const [allProductAvailabilityMonth, setAllProductAvailabilityMonth] = useState<string>(
    getDateInDomainTimezone("", "Y-m-d") as string
  )
  const [events, setEvents] = useState<Event[]>()
  const [isSelectedDate, setIsSelectedDate] = useState<Date | null>(
    data?.selectedDate ? new Date(data.selectedDate) : null
  )
  const formRef = useRef<FormikProps<ICalendarPageFilters>>(null)

  const { data: rentalProductInfoById, isValidating: isSelectedProductDetailValidating } = useSWR(
    selectedProduct || selectedProductId
      ? `selectedProductDetails-${selectedProduct || selectedProductId}`
      : null,
    async () => {
      try {
        const productId = selectedProduct || selectedProductId
        const { data } = await ProductServices.getProductInfoById(productId, {}, isIntegratedPage)
        return data
      } catch (e) {
        Sentry.captureException(e)
        console.error(e.response.data.message)
        showToast({ heading: e.response.data.message, type: "danger" })
      }
    },
    {
      revalidateOnFocus: false,
    }
  )

  useEffect(() => {
    if (rentalProductInfoById?.id) {
      if (rentalProductInfoById.first_available_date?.trim()) {
        setAvailabilityMonth(
          getDateInDomainTimezone(rentalProductInfoById.first_available_date, "Y-m-d") as string
        )
      } else {
        setAvailabilityMonth(getDateInDomainTimezone("", "Y-m-d") as string)
      }
    }
  }, [rentalProductInfoById])

  useEffect(() => {
    if (typeof eventsList !== "undefined") {
      refetchEventsList()
    }
  }, [debouncedSearchTerm, selectedSlotStatus])

  const SLOT_STATUS_FILTER_OPTIONS: SelectOptionType[] = useMemo(
    () => [
      {
        label: "Open & Closed",
        value: "all",
      },
      {
        label: "Open",
        value: SlotStatusEnum.OPEN,
      },
      {
        label: "Closed",
        value: SlotStatusEnum.CLOSED,
      },
    ],
    []
  )

  const {
    data: productAvailabilityPerMonthData,
    isLoading: isProductAvailabilityPerMonthDataLoading,
    mutate: refetchProductAvailabilityPerMonthData,
    isValidating: isproductAvailabilityPerMonthDataValidating,
  } = useSWR(
    availabilityMonth && selectedProduct
      ? `productAvailabilityPerMonthData-${selectedProduct}-${availabilityMonth}`
      : null,
    async () => {
      try {
        const { data: getProductAvailabilityForMonthResponse } =
          await OrderServices.getProductAvailabilityForMonth(
            selectedProduct ?? "",
            availabilityMonth as string,
            isIntegratedPage
          )
        if (getProductAvailabilityForMonthResponse) {
          return getProductAvailabilityForMonthResponse.data
        }
      } catch (e) {
        Sentry.captureException(e)
        console.error(e.response.data.message)
        showToast({ heading: e.response.data.message, type: "danger" })
      }
    },
    {
      revalidateOnFocus: false,
      refreshWhenHidden: false,
      shouldRetryOnError: false,
    }
  )

  useEffect(() => {
    if (!isProductAvailabilityPerMonthDataLoading) {
      if (data.calendar_for === "specific_Product") {
        cache.delete(`productAvailabilityPerMonthData-${selectedProduct}-${availabilityMonth}`)
        refetchProductAvailabilityPerMonthData()
      }
    }
  }, [availabilityMonth, data, selectedProduct])

  const {
    data: AllProductAvailibilityMonthData,
    isLoading: isAllProductAvailibilityMonthDataValidating,
  } = usePublicGetAllEvents({
    isIntegratedPage,
    dateRanges: getSplittedDateRanges({
      from_date: moment(allProductAvailabilityMonth).startOf("month").format("YYYY-MM-DD"),
      to_date: moment(allProductAvailabilityMonth).endOf("month").format("YYYY-MM-DD"),
    }),
    queryOptions: {
      enabled: Boolean(allProductAvailabilityMonth && data.calendar_for === "all_products"),
    },
  })

  // this is the events list for the big-calendar
  const {
    data: eventsList,
    isValidating: isEventsListValidating,
    mutate: refetchEventsList,
  } = useSWR(
    data.calendar_for === "all_products" &&
      data?.show_product_in_calendar === "directly_show_in_calendar"
      ? "eventsList"
      : null,
    async () => {
      try {
        const { data: getAllEventList } = await CalendarServices.getAllEvents(
          {
            from_date: selectedDateRange.from_date,
            to_date: selectedDateRange.to_date,
            search_text: debouncedSearchTerm || undefined,
            slot_status: selectedSlotStatus === "all" ? undefined : selectedSlotStatus,
            products: Array.isArray(formRef.current?.values.products)
              ? formRef.current?.values.products
                  .map((productOption) => productOption.value)
                  .filter((option) => option !== "all")
              : null,
            categories: Array.isArray(formRef.current?.values.products)
              ? (formRef.current?.values.categories || [])
                  .map((categoryOption) => categoryOption.value)
                  .filter((option) => option !== "all")
              : null,
          },
          isIntegratedPage
        )
        if (getAllEventList.length) {
          return getAllEventList
        }
        return []
      } catch (e) {
        Sentry.captureException(e)
        console.error(e.response.data.message)
        showToast({ heading: e.response.data.message, type: "danger" })
      }
    },
    {
      revalidateOnFocus: false,
    }
  )

  const initialValues: ICalendarPageFilters = useMemo(
    () => ({
      search: "",
      categories: [],
      status: "all",
      products: [],
    }),
    []
  )
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        search: Yup.string().optional(),
        categories: Yup.string().optional(),
        status: Yup.string().optional(),
        products: Yup.string().optional(),
      }),
    []
  )

  useEffect(() => {
    if (shouldUpdateFilterOptions && eventsList) {
      const filterOptions: { products: SelectOptionType[]; categories: SelectOptionType[] } = {
        products: [],
        categories: [],
      }
      if (eventsList) {
        eventsList.forEach((product) => {
          if (product?.category) {
            filterOptions.categories.push({
              label: product.category?.name,
              value: product.category?.id,
            })
          }
          filterOptions.products.push({
            label: product.product_name,
            value: product.product_id,
          })
        })
      }
      setCategoryFilterOptions((prev) => _.uniqBy([...prev, ...filterOptions.categories], "value"))
      setProductFilterOptions((prev) => _.uniqBy([...prev, ...filterOptions.products], "value"))
      setShouldUpdateFilterOptions(false)
    }
  }, [eventsList, shouldUpdateFilterOptions])

  const handleCalendarUpdate = (range: Date[] & { start: Date; end: Date }, view?: View) => {
    setShouldUpdateFilterOptions(true)
    if (view?.trim() === "month") {
      const startDate = getDateInDomainTimezone(range.start)
      const endDate = getDateInDomainTimezone(range.end)
      setSelectedDateRange((prev) => ({
        ...prev,
        from_date: getDateInDomainTimezone(startDate, "Y-m-d") as string,
        to_date: getDateInDomainTimezone(endDate, "Y-m-d") as string,
      }))
    } else if (view === "week") {
      const startDate = getDateInDomainTimezone(range[0])
      const endDate = getDateInDomainTimezone(range[range.length - 1])
      if (
        (getDateInDomainTimezone(startDate) as Moment).isBefore(
          getDateInDomainTimezone(selectedDateRange.from_date, "Y-m-d")
        ) ||
        (getDateInDomainTimezone(endDate) as Moment).isAfter(
          getDateInDomainTimezone(selectedDateRange.to_date, "Y-m-d")
        )
      ) {
        setSelectedDateRange((prev) => ({
          ...prev,
          from_date: getDateInDomainTimezone(startDate, "Y-m-d") as string,
          to_date: getDateInDomainTimezone(endDate, "Y-m-d") as string,
        }))
      }
    } else if (view === "day") {
      const selectedDate = getDateInDomainTimezone(range[0])
      if (
        moment(selectedDate).isBefore(
          getDateInDomainTimezone(selectedDateRange.from_date, "Y-m-d")
        ) ||
        (getDateInDomainTimezone(selectedDate) as Moment).isAfter(
          getDateInDomainTimezone(selectedDateRange.to_date, "Y-m-d")
        )
      ) {
        setSelectedDateRange((prev) => ({
          ...prev,
          from_date: getDateInDomainTimezone(selectedDate, "Y-m-d") as string,
          to_date: getDateInDomainTimezone(selectedDate, "Y-m-d") as string,
        }))
      }
    }
  }

  useEffect(() => {
    if (eventsList && selectedDateRange) {
      refetchEventsList()
    }
  }, [selectedDateRange, eventsList, refetchEventsList])

  useEffect(() => {
    if (eventsList?.length) {
      const formatedEventsList: Event[] = []
      eventsList.forEach((product) => {
        product.availability.forEach((dayAvailability) => {
          if (dayAvailability.available) {
            formatedEventsList.push({
              title: product.product_name,
              start: moment(
                `${dayAvailability.slot_date} ${dayAvailability.slots[0].start_time}`,
                "YYYY-MM-DD hh:mmA"
              ).toDate(),
              end: moment(
                `${dayAvailability.slot_date} ${dayAvailability.slots[dayAvailability.slots.length - 1].end_time}`,
                "YYYY-MM-DD hh:mmA"
              ).toDate(),
              resource: {
                product_name: product.product_name,
                product_id: product.product_id,
                availability: dayAvailability,
              },
            })
          }
        })
      })
      setEvents(formatedEventsList)
    } else {
      setEvents(undefined)
    }
  }, [eventsList])

  const getProductDates = useMemo(() => {
    const allProductsAvailableDates: Date[] = []
    const allProductsUnavailableDates: Date[] = []
    AllProductAvailibilityMonthData?.map((product) => {
      product.availability.map((date: EventAvailablilityADayType) => {
        if (date.available) {
          allProductsAvailableDates.push(moment(date.slot_date).toDate())
        }
      })
    })

    AllProductAvailibilityMonthData?.map((product) => {
      product.availability.map((date: EventAvailablilityADayType) => {
        if (!date.available) {
          const index = allProductsAvailableDates.findIndex((dt) => {
            return dt.getTime() === moment(date.slot_date).toDate().getTime()
          })
          if (index <= -1) {
            allProductsUnavailableDates.push(moment(date.slot_date).toDate())
          }
        }
      })
    })

    return {
      allProductsAvailableDates,
      allProductsUnavailableDates,
    }
  }, [AllProductAvailibilityMonthData])

  const getproductUnavailableDates = useMemo(() => {
    const unavailabelDates: Date[] = []
    const currentDate = getDateInDomainTimezone()

    Object.keys(productAvailabilityPerMonthData ?? {}).forEach((date) => {
      const monthlyDate = getDateInDomainTimezone(date)
      const isDateInPast = (monthlyDate as Moment)?.isBefore(currentDate, "day")
      if (
        (productAvailabilityPerMonthData && !productAvailabilityPerMonthData[date].available) ||
        (isDateInPast && !(monthlyDate as Moment)?.isSame(currentDate, "day"))
      ) {
        unavailabelDates.push(toLocalDate(moment(date)))
      }
    })
    return unavailabelDates
  }, [productAvailabilityPerMonthData])

  const getproductavailableDates = useMemo(() => {
    const availabelDates: Date[] = []
    Object.keys(productAvailabilityPerMonthData ?? {}).forEach((date) => {
      const isDateInFuture =
        (getDateInDomainTimezone(date).valueOf() as number) -
          (getDateInDomainTimezone().valueOf() as number) >
        0
      if (
        productAvailabilityPerMonthData &&
        Boolean(productAvailabilityPerMonthData[date].available) &&
        isDateInFuture
      ) {
        availabelDates.push(toLocalDate(moment(date)))
      }
    })
    return availabelDates
  }, [productAvailabilityPerMonthData])

  const handleProductModal = (isCancel = false) => {
    if (window !== window.parent && isCancel) {
      const json = {
        msg: "hideFullScreenPopUp",
        isFrom: "smartRez",
      }
      window.parent.postMessage(JSON.stringify(json), "*")
    }
  }

  const handleModalClose = (isCancel = false) => {
    setShow(false)
    handleProductModal(isCancel)
  }

  const goToBackStep = () => {
    setShowCheckOutModal(false)
  }

  const productDetails = {
    product_description_view: productView,
  }

  const handleFilterOptionChange = (
    selectOptions: SelectOptionType[],
    clickedOption: IClickedOption,
    fieldName: string,
    allOptions: SelectOptionType[]
  ) => {
    if (formRef.current) {
      const { setFieldValue } = formRef.current
      if (Array.isArray(selectOptions)) {
        if (clickedOption.action === "select-option" && clickedOption.option.value === "all") {
          setFieldValue(fieldName, allOptions)
        } else if (
          clickedOption.action === "deselect-option" &&
          clickedOption.option.value === "all"
        ) {
          setFieldValue(fieldName, [])
        } else if (
          _.findIndex(selectOptions, (option) => option.value === "all") < 0 &&
          _.differenceWith(
            allOptions.filter((option) => option.value !== "all"),
            selectOptions,
            _.isEqual
          ).length === 0
        ) {
          setFieldValue(fieldName, allOptions)
        } else if (
          clickedOption.option.value !== "all" &&
          _.findIndex(selectOptions, (option) => option.value === "all") >= 0
        ) {
          setFieldValue(
            fieldName,
            selectOptions.filter((option) => option.value !== "all")
          )
        } else {
          setFieldValue(fieldName, selectOptions)
        }
      } else {
        setFieldValue(fieldName, [])
      }
    }
  }

  const onDateSelect = (selectedDate: Date) => {
    if (window !== window.parent) {
      const json = {
        msg: "openFullScreenPopup",
        url: `${document.URL}${document.URL.includes("?") ? "&" : "?"}nextStep=true&selectedDate=${selectedDate.toISOString()}`,
        isFrom: "smartRez",
      }
      window.parent.postMessage(JSON.stringify(json), "*")
    } else {
      setIsSelectedDate?.(selectedDate)
      if (selectedProduct) {
        setShow(true)
      } else {
        setShowSelectModal(true)
      }
    }
  }

  useEffect(() => {
    if (nextStep && selectedProduct) {
      setShow(true)
    } else if (nextStep) {
      setShowSelectModal(true)
    }
  }, [selectedProduct, nextStep])

  const needsProduct =
    data.calendar_for === "specific_Product" && !(availabilityMonth && selectedProduct)

  const isSmallCalendarLoading = useMemo(() => {
    return (
      (!show && isproductAvailabilityPerMonthDataValidating) ||
      isAllProductAvailibilityMonthDataValidating ||
      needsProduct ||
      isSelectedProductDetailValidating
    )
  }, [
    show,
    isAllProductAvailibilityMonthDataValidating,
    isproductAvailabilityPerMonthDataValidating,
    isSelectedProductDetailValidating,
    needsProduct,
  ])

  return (
    <>
      {!nextStep && (
        <>
          {showBigCalendar ? (
            <div className={"relative mt-6 h-[84vh] w-full p-6"}>
              <MonoCalendar
                events={events}
                isEventsLoading={isEventsListValidating}
                onRangeChange={handleCalendarUpdate as any}
                onSelectEvent={(event) => {
                  setShowProductSelectedId(event?.resource?.product_id)
                  setShow(true)
                }}
              />
              <div
                className={
                  "absolute -left-0.5 top-[52px] w-full overflow-hidden bg-transparent px-0.5 pb-0 pt-0.5"
                }
              >
                <div className={"mt-2 w-full rounded-t-md bg-whiteBlack-100 p-4 shadow-sm"}>
                  <Formik
                    innerRef={formRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={() => {}}
                    enableReinitialize
                  >
                    {({ getFieldProps, setFieldValue, values }) => (
                      <Form>
                        <div className={"flex flex-row items-center justify-start gap-4"}>
                          <FormGroup
                            name={"search"}
                            className={"w-full max-w-60"}
                            renderError={false}
                          >
                            <TextInput
                              {...getFieldProps("search_text")}
                              placeholder={"Quick Search"}
                              autoComplete={"off"}
                              icon={() => (
                                <SearchIcon width={18} height={18} className={"ml-1.5"} />
                              )}
                              onChange={(e) => {
                                setFieldValue("search_text", e.target.value)
                                setSearchTerm(e.target.value)
                              }}
                            />
                          </FormGroup>
                          <MonoSelectWithCheckBox
                            inputId={"categories"}
                            {...getFieldProps("categories")}
                            isMulti
                            options={categoryFilterOptions}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            menuPosition={"fixed"}
                            className={"z-10 min-w-[174px] max-w-[230px]"}
                            hideClearIcon
                            showSelectedOptions={false}
                            placeholder={"Select Categories"}
                            onChange={(
                              options: SelectOptionType[],
                              selectedOption: IClickedOption
                            ) => {
                              handleFilterOptionChange(
                                options,
                                selectedOption,
                                "categories",
                                categoryFilterOptions
                              )
                              setTimeout(() => refetchEventsList())
                            }}
                            selectedCountSuffixLabel={
                              values.categories.length > 1 ? "Categories" : "Category"
                            }
                          />
                          <MonoSelect
                            {...getFieldProps("slot_status")}
                            options={SLOT_STATUS_FILTER_OPTIONS}
                            inputId={"slot_status"}
                            className={"z-10 max-w-[170px]"}
                            onValueChange={(selectedOption) =>
                              setSelectedSlotStatus(selectedOption.value)
                            }
                            menuPosition={"fixed"}
                          />
                          <MonoSelectWithCheckBox
                            inputId={"products"}
                            {...getFieldProps("products")}
                            isMulti
                            options={productFilterOptions}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            menuPosition={"fixed"}
                            className={"z-10 min-w-[174px] max-w-[230px]"}
                            hideClearIcon
                            showSelectedOptions={false}
                            placeholder={"Select Products"}
                            onChange={(
                              options: SelectOptionType[],
                              selectedOption: IClickedOption
                            ) => {
                              handleFilterOptionChange(
                                options,
                                selectedOption,
                                "products",
                                productFilterOptions
                              )
                              setTimeout(() => refetchEventsList())
                            }}
                            selectedCountSuffixLabel={
                              values.products.length > 1 ? "Product" : "Products"
                            }
                          />
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          ) : (
            <div className={"w-[350px]"}>
              <MonoInlineDatePicker
                onMonthChange={(props) => {
                  if (data.calendar_for === "all_products") {
                    setAllProductAvailabilityMonth(
                      getDateInDomainTimezone(props, "Y-m-d") as string
                    )
                  } else {
                    setAvailabilityMonth(getDateInDomainTimezone(props, "Y-m-d") as string)
                  }
                }}
                isSelectedDate={toLocalDate(moment())}
                inline
                loading={isSmallCalendarLoading}
                showSpinner={isSmallCalendarLoading}
                onChange={onDateSelect}
                popperClassName={"px-4 pt-4 pb-3 m-6 !border-gray-100 shadow !inline-grid"}
                excludeDates={
                  selectedProduct
                    ? getproductUnavailableDates
                    : data.calendar_for === "all_products"
                      ? getProductDates?.allProductsUnavailableDates
                      : []
                }
                highlightDates={
                  selectedProduct
                    ? getproductavailableDates
                    : data.calendar_for === "all_products"
                      ? getProductDates?.allProductsAvailableDates
                      : []
                }
                minDate={toLocalDate()}
                openToDate={
                  data.calendar_for === "all_products" && allProductAvailabilityMonth
                    ? toLocalDate(moment(allProductAvailabilityMonth))
                    : availabilityMonth
                      ? toLocalDate(moment(availabilityMonth))
                      : undefined
                }
              >
                <div className={"mt-2 flex items-center justify-start gap-2"}>
                  <span className={"text12Semibold flex items-center text-green-600"}>
                    <span className={"mr-1 h-1 w-1 rounded-full bg-green-600"} />
                    Available
                  </span>
                  <span className={"text12Semibold flex items-center text-neutral-500"}>
                    <span className={"mr-1 h-1 w-1 rounded-full bg-neutral-500"} />
                    Unavailable
                  </span>
                  <span className={"text12Semibold flex items-center text-primary-600"}>
                    <span className={"mr-1 h-1 w-1 rounded-full bg-primary-600 "} />
                    Selected
                  </span>
                  <span className={"text12Semibold flex items-center text-orange-450"}>
                    <span className={"mr-1 h-1 w-1 rounded-full text-orange-450"} />
                    Today
                  </span>
                </div>
              </MonoInlineDatePicker>
              {((getproductavailableDates?.length === 0 &&
                data.calendar_for === "specific_Product") ||
                (getProductDates?.allProductsAvailableDates.length === 0 &&
                  data.calendar_for === "all_products")) &&
                !isSmallCalendarLoading && <NoSlotsAvailable />}
            </div>
          )}
        </>
      )}
      <SelectProductModal
        show={showSelectModal}
        setShow={setShowSelectModal}
        isIntegratedPage={isIntegratedPage}
        productDetails={productDetails}
        domainDetails={domainDetails}
        handleClose={handleProductModal}
        isSelectedDate={isSelectedDate}
        selectedProductData={rentalProductInfoById}
        isSelectedProductDetailValidating={isSelectedProductDetailValidating}
      />

      {rentalProductInfoById?.is_dynamic_rental ? (
        <ProductRentalBuyModal
          show={show}
          selectedProductData={rentalProductInfoById}
          selectedProduct={rentalProductInfoById?.id || selectedProduct}
          pagePreview={productDetails?.product_description_view || "default_view"}
          goToBackStep={goToBackStep}
          closeModal={handleModalClose}
          onSuccess={(cartId: string) => {
            setShowCheckOutModal(true)
            setcartId(cartId)
          }}
          handleHomeButtonClick={handleModalClose}
          isIntegratedPage={isIntegratedPage}
          domainDetails={domainDetails}
          isSelectedDate={isSelectedDate}
        />
      ) : (
        <ProductBuyModal
          show={show}
          selectedProduct={selectedProductId || selectedProduct}
          pagePreview={productView}
          closeModal={handleModalClose}
          onSuccess={(cartId: string) => {
            setShowCheckOutModal(true)
            setcartId(cartId)
          }}
          handleHomeButtonClick={handleModalClose}
          isIntegratedPage={isIntegratedPage}
          domainDetails={domainDetails}
          isSelectedDate={isSelectedDate}
          selectedProductData={rentalProductInfoById}
        />
      )}

      {showCheckoutModal && (
        <CheckoutModal
          show={showCheckoutModal}
          handleModalClose={() => {
            setShowCheckOutModal(false)
            handleModalClose(true)
          }}
          goToBackStep={goToBackStep}
          cartId={cartId}
          onsucess={(orderDataResponse: GetOrderDetailsResponseType | undefined) => {
            setShowCheckOutModal(false)
            handleModalClose()
            setOrderConfirmModal(true)
            if (orderDataResponse) {
              setOrdertData(orderDataResponse)
            }
          }}
          isIntegratedPage={isIntegratedPage}
          domainDetails={domainDetails}
          hideCustomerDetails={hideCustomerDetails}
        />
      )}
      {orderData && orderConfirmModal && (
        <OrderConfirmModal
          orderData={orderData}
          show={orderConfirmModal}
          setShow={setOrderConfirmModal}
          handleModalClose={() => handleProductModal(true)}
        />
      )}
    </>
  )
}

export default CalendarPreview
